/**
 * Authentication Store with Supabase Integration
 *
 * This store manages all authentication state using Supabase Auth.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { supabase } from '@/lib/supabase';
import { Logger, LogCategory, logAuthEvent, logStateChange } from '@/utils/logger';
import { AuthError, ErrorCode } from '@/utils/errorHandler';
import { DEFAULT_PHONE_NUMBER_GROUPS } from '@/constants';
import type { User, AuthProvider, Company, SignupData } from '@/types';
import type { AuthError as SupabaseAuthError } from '@supabase/supabase-js';

// ============================================================================
// Types
// ============================================================================

interface AuthState {
  // State
  user: User | null;
  company: Company | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  lastActivity: number | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithWorkOS: (code: string) => Promise<void>;
  handleOAuthCallback: (code: string, state?: string) => Promise<void>;
  signup: (signupData: SignupData) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  updateUser: (updates: Partial<User>) => void;
  updateCompany: (updates: Partial<Company>) => void;
  checkSession: () => Promise<boolean>;
  refreshSession: () => Promise<void>;
  initializeAuth: () => Promise<void>;
}

// ============================================================================
// Helper Functions
// ============================================================================

function transformUser(dbUser: any, dbCompany: any): { user: User; company: Company } {
  const user: User = {
    id: dbUser.id,
    email: dbUser.email,
    name: dbUser.name,
    role: dbUser.role,
    companyId: dbUser.company_id,
    avatarUrl: dbUser.avatar_url,
    createdAt: dbUser.created_at,
    lastLoginAt: dbUser.last_login_at,
    isActive: dbUser.is_active,
    authProvider: dbUser.auth_provider as AuthProvider,
    isSuperAdmin: dbUser.is_super_admin,
    emailVerified: dbUser.email_verified,
  };

  const company: Company = {
    id: dbCompany.id,
    name: dbCompany.name,
    domain: dbCompany.domain,
    credits: dbCompany.credits,
    plan: dbCompany.plan,
    isInternal: dbCompany.is_internal,
    industry: dbCompany.industry,
    organizationSize: dbCompany.organization_size,
    phoneNumber: dbCompany.phone_number,
    hasUsedTrial: dbCompany.has_used_trial,
    address: dbCompany.address_street ? {
      street: dbCompany.address_street,
      city: dbCompany.address_city,
      state: dbCompany.address_state,
      zipCode: dbCompany.address_zip_code,
      country: dbCompany.address_country,
    } : undefined,
    billingEmail: dbCompany.billing_email,
    createdAt: dbCompany.created_at,
    updatedAt: dbCompany.updated_at,
    settings: {
      notificationFrequency: dbCompany.notification_frequency,
      emailNotifications: dbCompany.email_notifications,
      smsNotifications: dbCompany.sms_notifications,
      notifyOnBillingChanges: dbCompany.notify_on_billing_changes,
      notifyOnPhoneFlagged: dbCompany.notify_on_phone_flagged,
      notifyOnCreditsLow: dbCompany.notify_on_credits_low,
      lowCreditThreshold: dbCompany.low_credit_threshold,
      autoRecharge: dbCompany.auto_recharge,
      rechargeAmount: dbCompany.recharge_amount,
      rechargeThreshold: dbCompany.recharge_threshold,
      notificationsEnabled: dbCompany.notifications_enabled,
    },
  };

  return { user, company };
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial State
        user: null,
        company: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        lastActivity: null,

        // Initialize auth on app load
        initializeAuth: async () => {
          Logger.info(LogCategory.AUTH, 'Initializing authentication', 'initializeAuth');

          try {
            const { data: { session } } = await supabase.auth.getSession();

            if (session) {
              Logger.info(LogCategory.AUTH, 'Session found, loading profile via RPC', 'initializeAuth', { userId: session.user.id });

              // Load user + company data via SECURITY DEFINER RPC to bypass RLS
              const { data: profile, error: profileError } = await (supabase.rpc as any)('get_my_profile');

              if (profileError) {
                Logger.error(LogCategory.AUTH, 'Failed to load profile in initializeAuth', 'initializeAuth', { userId: session.user.id }, profileError);
                throw profileError;
              }

              const dbUser = (profile as any).user;
              const dbCompany = (profile as any).company;

              Logger.info(LogCategory.AUTH, 'Profile loaded successfully', 'initializeAuth', { companyId: dbCompany.id });

              const { user, company } = transformUser(dbUser, dbCompany);

              set({
                user,
                company,
                token: session.access_token,
                isAuthenticated: true,
                lastActivity: Date.now(),
              });

              Logger.info(LogCategory.AUTH, 'Auth initialized successfully', 'initializeAuth', {
                userId: user.id,
                companyId: company.id,
              });
            }
          } catch (error) {
            Logger.error(LogCategory.AUTH, 'Failed to initialize auth', 'initializeAuth', undefined, error);
            // Don't immediately sign out - keep existing auth state
            // This prevents logout loops when data fetching temporarily fails
            Logger.warn(LogCategory.AUTH, 'Keeping existing auth state despite init error', 'initializeAuth');
          }
        },

        // Login with email/password
        login: async (email: string, password: string) => {
          Logger.info(LogCategory.AUTH, 'Login attempt started', 'login', {
            email: email.substring(0, 3) + '***',
          });

          set({ isLoading: true, error: null });

          try {
            // Sign in with Supabase
            const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
              email,
              password,
            });

            if (authError) {
              Logger.warn(LogCategory.AUTH, 'Login failed', 'login', { error: authError.message });
              throw new AuthError(
                ErrorCode.AUTH_INVALID_CREDENTIALS,
                'Invalid credentials',
                authError.message
              );
            }

            // Get user profile + company via SECURITY DEFINER RPC
            const { data: profile, error: profileError } = await (supabase.rpc as any)('get_my_profile');

            if (profileError) {
              Logger.error(LogCategory.AUTH, 'Failed to fetch profile', 'login', { userId: authData.user.id }, profileError);
              throw new Error(`Failed to fetch user profile: ${profileError.message}`);
            }

            const dbUser = (profile as any).user;
            const dbCompany = (profile as any).company;

            Logger.info(LogCategory.AUTH, 'Profile fetched', 'login', { userId: dbUser.id, companyId: dbCompany.id });

            const { user, company } = transformUser(dbUser, dbCompany);

            set({
              user,
              company,
              token: authData.session.access_token,
              isAuthenticated: true,
              isLoading: false,
              error: null,
              lastActivity: Date.now(),
            });

            logAuthEvent('login', undefined, true, { userId: user.id });
            Logger.info(LogCategory.AUTH, 'Login successful', 'login', {
              userId: user.id,
              role: user.role,
            });
          } catch (error: any) {
            const errorMessage = error?.message || 'Login failed';
            set({ error: errorMessage, isLoading: false });
            logAuthEvent('login', undefined, false, undefined, error);
            Logger.error(LogCategory.AUTH, 'Login error', 'login', undefined, error);
            throw error;
          }
        },

        // Google OAuth
        loginWithGoogle: async () => {
          Logger.info(LogCategory.AUTH, 'Google OAuth initiated', 'loginWithGoogle');

          try {
            const { error } = await supabase.auth.signInWithOAuth({
              provider: 'google',
              options: {
                redirectTo: `${window.location.origin}/auth/callback`,
              },
            });

            if (error) throw error;

            Logger.info(LogCategory.AUTH, 'Redirecting to Google', 'loginWithGoogle');
          } catch (error) {
            Logger.error(LogCategory.AUTH, 'Google OAuth error', 'loginWithGoogle', undefined, error);
            throw error;
          }
        },

        // WorkOS SSO
        loginWithWorkOS: async (code: string) => {
          Logger.info(LogCategory.AUTH, 'WorkOS SSO initiated', 'loginWithWorkOS');
          // TODO: Implement WorkOS integration
          throw new Error('WorkOS not yet implemented');
        },

        // Handle OAuth callback
        handleOAuthCallback: async (code: string, state?: string) => {
          Logger.info(LogCategory.AUTH, 'Handling OAuth callback', 'handleOAuthCallback');

          try {
            // Supabase handles this automatically
            // Just need to load user data
            await get().initializeAuth();
          } catch (error) {
            Logger.error(LogCategory.AUTH, 'OAuth callback error', 'handleOAuthCallback', undefined, error);
            throw error;
          }
        },

        // Sign up
        signup: async (signupData: SignupData) => {
          Logger.info(LogCategory.AUTH, 'Signup attempt started', 'signup', {
            email: signupData.email.substring(0, 3) + '***',
          });

          set({ isLoading: true, error: null });
          isSigningUp = true;

          try {
            // 1. Sign up user with Supabase Auth FIRST (to get authenticated session)
            const { data: authData, error: authError } = await supabase.auth.signUp({
              email: signupData.email,
              password: signupData.password,
              options: {
                data: {
                  name: signupData.name,
                },
              },
            });

            if (authError) {
              Logger.error(LogCategory.AUTH, 'Auth signup failed', 'signup', undefined, authError);
              throw authError;
            }

            if (!authData.user) {
              throw new Error('User creation failed');
            }

            const hasSession = !!authData.session;
            const requiresConfirmation = !hasSession;

            Logger.info(LogCategory.AUTH, 'Auth user created, creating profile via RPC', 'signup', {
              userId: authData.user.id,
              hasSession,
              domain: signupData.domain,
            });

            // 2. Create company, user profile, and default groups via server-side RPC
            //    This bypasses RLS issues during signup
            const { data: company, error: rpcError } = await (supabase.rpc as any)('handle_signup', {
              p_user_id: authData.user.id,
              p_email: signupData.email,
              p_name: signupData.name,
              p_company_name: signupData.companyName,
              p_domain: signupData.domain,
              p_phone_number: signupData.phoneNumber || null,
              p_industry: signupData.industry || null,
              p_organization_size: signupData.organizationSize || null,
            });

            Logger.info(LogCategory.AUTH, 'Signup RPC result', 'signup', {
              success: !rpcError,
              companyId: (company as any)?.id,
              error: rpcError?.message,
            });

            if (rpcError) {
              Logger.error(LogCategory.AUTH, 'Signup profile creation failed', 'signup', undefined, rpcError);
              await supabase.auth.signOut();
              throw rpcError;
            }

            // 5. If email confirmation is required, stop here
            if (requiresConfirmation) {
              Logger.info(LogCategory.AUTH, 'Signup successful - email confirmation required', 'signup', {
                userId: authData.user.id,
              });
              set({ isLoading: false, error: null });
              logAuthEvent('signup', undefined, true, {
                userId: authData.user.id,
                requiresConfirmation: true,
              });
              return;
            }

            // 6. Auto-login if we have a session
            const user: User = {
              id: authData.user.id,
              email: signupData.email,
              name: signupData.name,
              role: 'admin',
              companyId: (company as any).id,
              isActive: true,
              authProvider: 'local',
              createdAt: authData.user.created_at,
              emailVerified: authData.user.email_confirmed_at ? true : false,
            };

            const transformedCompany = transformUser({ ...user, companies: company } as any, company as any).company;

            set({
              user,
              company: transformedCompany,
              token: authData.session!.access_token,
              isAuthenticated: true,
              isLoading: false,
              error: null,
              lastActivity: Date.now(),
            });

            logAuthEvent('signup', undefined, true, { userId: user.id });
            Logger.info(LogCategory.AUTH, 'Signup successful - auto-login completed', 'signup', {
              userId: user.id,
              companyId: (company as any).id,
            });
          } catch (error: any) {
            const errorMessage = error?.message || 'Signup failed';
            set({ error: errorMessage, isLoading: false });
            logAuthEvent('signup', undefined, false, undefined, error);
            Logger.error(LogCategory.AUTH, 'Signup error', 'signup', undefined, error);
            throw error;
          } finally {
            isSigningUp = false;
          }
        },

        // Logout
        logout: async () => {
          Logger.info(LogCategory.AUTH, 'Logout initiated', 'logout');

          try {
            const { error } = await supabase.auth.signOut();
            if (error) throw error;

            set({
              user: null,
              company: null,
              token: null,
              isAuthenticated: false,
              error: null,
              lastActivity: null,
            });

            logAuthEvent('logout', undefined, true);
            Logger.info(LogCategory.AUTH, 'Logout successful', 'logout');
          } catch (error) {
            Logger.error(LogCategory.AUTH, 'Logout error', 'logout', undefined, error);
          }
        },

        // Clear error
        clearError: () => {
          set({ error: null });
        },

        // Update user
        updateUser: (updates: Partial<User>) => {
          const currentUser = get().user;
          if (!currentUser) return;

          set({ user: { ...currentUser, ...updates } });
          Logger.debug(LogCategory.AUTH, 'User updated', 'updateUser', updates);
        },

        // Update company
        updateCompany: (updates: Partial<Company>) => {
          const currentCompany = get().company;
          if (!currentCompany) return;

          set({ company: { ...currentCompany, ...updates } });
          Logger.debug(LogCategory.AUTH, 'Company updated', 'updateCompany', updates);
        },

        // Check session
        checkSession: async () => {
          const { data: { session } } = await supabase.auth.getSession();
          return !!session;
        },

        // Refresh session
        refreshSession: async () => {
          Logger.info(LogCategory.AUTH, 'Refreshing session', 'refreshSession');

          try {
            const { data: { session }, error } = await supabase.auth.refreshSession();

            if (error) throw error;

            if (session) {
              set({
                token: session.access_token,
                lastActivity: Date.now(),
              });
              Logger.info(LogCategory.AUTH, 'Session refreshed', 'refreshSession');
            }
          } catch (error) {
            Logger.error(LogCategory.AUTH, 'Session refresh failed', 'refreshSession', undefined, error);
          }
        },
      }),
      {
        name: 'repucall_auth',
        partialize: (state) => ({
          user: state.user,
          company: state.company,
          isAuthenticated: state.isAuthenticated,
        }),
      }
    ),
    { name: 'AuthStore' }
  )
);

// ============================================================================
// Auth State Change Listener
// ============================================================================

// Track last event to prevent rapid-fire loops
let lastAuthEvent: { event: string; timestamp: number } | null = null;
const MIN_EVENT_INTERVAL = 100; // Minimum ms between processing same event type

// Flag to suppress initializeAuth during signup (signup creates rows itself)
let isSigningUp = false;

// Listen to Supabase auth state changes
supabase.auth.onAuthStateChange(async (event, session) => {
  const now = Date.now();

  // Prevent processing duplicate events in rapid succession
  if (lastAuthEvent &&
      lastAuthEvent.event === event &&
      now - lastAuthEvent.timestamp < MIN_EVENT_INTERVAL) {
    Logger.debug(LogCategory.AUTH, 'Skipping duplicate auth event', 'onAuthStateChange', {
      event,
      timeSinceLastEvent: now - lastAuthEvent.timestamp
    });
    return;
  }

  lastAuthEvent = { event, timestamp: now };

  Logger.info(LogCategory.AUTH, 'Auth state changed', 'onAuthStateChange', { event });

  const store = useAuthStore.getState();

  if (event === 'SIGNED_IN' && session) {
    // Skip if signup is in progress — it will set state itself after creating rows
    if (isSigningUp) {
      Logger.debug(LogCategory.AUTH, 'Skipping initializeAuth during signup', 'onAuthStateChange');
      return;
    }
    // Only reload if not already authenticated with this user
    if (!store.isAuthenticated || store.user?.id !== session.user.id) {
      await store.initializeAuth();
    }
  } else if (event === 'SIGNED_OUT') {
    // Only clear if currently authenticated
    if (store.isAuthenticated) {
      Logger.warn(LogCategory.AUTH, 'SIGNED_OUT event received - clearing auth state', 'onAuthStateChange', {
        wasAuthenticated: store.isAuthenticated,
        userId: store.user?.id
      });
      useAuthStore.setState({
        user: null,
        company: null,
        token: null,
        isAuthenticated: false,
        error: null,
        lastActivity: null,
      });
      Logger.info(LogCategory.AUTH, 'User signed out', 'onAuthStateChange');
    } else {
      Logger.debug(LogCategory.AUTH, 'SIGNED_OUT event received but user was not authenticated', 'onAuthStateChange');
    }
  } else if (event === 'TOKEN_REFRESHED' && session) {
    useAuthStore.setState({
      token: session.access_token,
      lastActivity: Date.now(),
    });
  }
});

// ============================================================================
// Convenience Hooks
// ============================================================================

export const useUser = () => useAuthStore((state) => state.user);
export const useCompany = () => useAuthStore((state) => state.company);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useIsAdmin = () => {
  const user = useUser();
  return user?.role === 'admin';
};
export const useIsSuperAdmin = () => {
  const user = useUser();
  return user?.isSuperAdmin === true;
};
export const useCanEdit = () => {
  const user = useUser();
  return user?.role !== 'viewer';
};
